// Filtros, píldoras y orden de la cartera del vendedor. El servidor solo busca por inicio del
// nombre; todo lo demás mira lo cargado y la pantalla lo rotula. Puro.
import { ClientLevelEnum } from '@fiado/type-kit/bin/loanScoring/enums/ClientLevelEnum';
import type {
    PortalCustomerRow,
    PortalCustomersQuery,
    PortalCustomerStatus,
} from '@/api/services/retail-wizard/contract';
import { mxDay } from '@/lib/dateHelpers';
import type { DataTableSort } from '@/shared/patterns/DataTable';

/** Los niveles que se filtran; `NONE` junta al que no tiene nivel con el no elegible. */
export type LevelFilter =
    | ClientLevelEnum.GOLD
    | ClientLevelEnum.SILVER
    | ClientLevelEnum.BRONZE
    | 'NONE';

export interface PortalCustomersFilters {
    /** Nombre (viaja al servidor) o teléfono (sobre lo cargado). */
    query: string;
    levels: LevelFilter[];
    trends: PortalCustomerStatus[];
    minPurchases: string;
    maxPurchases: string;
    /** Día `YYYY-MM-DD` de CDMX. */
    lastFrom: string;
    lastTo: string;
}

export const EMPTY_FILTERS: PortalCustomersFilters = {
    query: '',
    levels: [],
    trends: [],
    minPurchases: '',
    maxPurchases: '',
    lastFrom: '',
    lastTo: '',
};

export type CustomersSegment =
    | 'all'
    | 'active'
    | 'dormant'
    | 'repurchase'
    | 'recurrent'
    | 'skCredit'
    | 'accountOnly';

export type SegmentCounts = Record<CustomersSegment, number>;

export type SortKey =
    | 'customerName'
    | 'status'
    | 'level'
    | 'purchasesCount'
    | 'totalSoldCents'
    | 'avgTicketCents'
    | 'daysSinceLastPurchase'
    | 'favoriteStoreName';

export type SortState = DataTableSort<SortKey> & { key: SortKey };

/** El orden del servidor: alfabético. */
export const DEFAULT_SORT: SortState = { key: 'customerName', direction: 'asc' };

/** Largo máximo que acepta el servidor en `q`. */
const QUERY_MAX_LENGTH = 80;

const TEXT_SORT_KEYS = new Set<SortKey>(['customerName', 'favoriteStoreName']);

const STATUS_RANK: Record<PortalCustomerStatus, number> = { ACTIVE: 3, REGULAR: 2, DORMANT: 1 };

const LEVEL_RANK: Partial<Record<string, number>> = {
    [ClientLevelEnum.GOLD]: 3,
    [ClientLevelEnum.SILVER]: 2,
    [ClientLevelEnum.BRONZE]: 1,
};

const SEGMENT_TEST: Record<CustomersSegment, (row: PortalCustomerRow) => boolean> = {
    all: () => true,
    active: (row) => row.status === 'ACTIVE',
    dormant: (row) => row.status === 'DORMANT',
    repurchase: (row) => row.repurchaseCandidate,
    recurrent: (row) => row.recurrent,
    skCredit: (row) => row.boughtOnSkCredit,
    accountOnly: (row) => row.accountOnly,
};

/** Igual que la llave del servidor: sin diacríticos, en mayúsculas y con espacios colapsados. */
export function normalizeName(name: string | null | undefined): string {
    if (!name) return '';
    return name.normalize('NFD').replace(/\p{M}/gu, '').toUpperCase().replace(/\s+/g, ' ').trim();
}

/** Un término sin letras es un teléfono: el servidor no lo sabe buscar. */
function isPhoneTerm(term: string): boolean {
    return term !== '' && !/\p{L}/u.test(term);
}

/** Lo que viaja a `GET /portal/customers`: solo el nombre. */
export function serverQueryOf(filters: PortalCustomersFilters): Pick<PortalCustomersQuery, 'q'> {
    const term = filters.query.trim();
    if (term === '' || isPhoneTerm(term)) return {};
    return { q: term.slice(0, QUERY_MAX_LENGTH) };
}

/** El nivel que se filtra; lo que no es metal cae en «Sin nivel». */
export function levelFilterOf(clientLevel: string | null): LevelFilter {
    if (
        clientLevel === ClientLevelEnum.GOLD ||
        clientLevel === ClientLevelEnum.SILVER ||
        clientLevel === ClientLevelEnum.BRONZE
    ) {
        return clientLevel;
    }
    return 'NONE';
}

function matchesQuery(row: PortalCustomerRow, term: string): boolean {
    if (term === '') return true;
    if (isPhoneTerm(term)) {
        const digits = term.replace(/\D/g, '');
        return digits === '' || (row.customerPhone ?? '').replace(/\D/g, '').includes(digits);
    }
    return normalizeName(row.customerName).startsWith(normalizeName(term));
}

function inRange(value: number, min: string, max: string): boolean {
    if (min !== '' && value < Number(min)) return false;
    if (max !== '' && value > Number(max)) return false;
    return true;
}

function inDayRange(iso: string | null, from: string, to: string): boolean {
    if (from === '' && to === '') return true;
    if (iso === null) return false;
    const day = mxDay(iso);
    if (from !== '' && day < from) return false;
    if (to !== '' && day > to) return false;
    return true;
}

/** Los filtros del panel sobre lo cargado. Repetir lo que ya filtró el servidor no cambia nada. */
export function filterCustomers(
    rows: readonly PortalCustomerRow[],
    filters: PortalCustomersFilters,
): PortalCustomerRow[] {
    const term = filters.query.trim();
    return rows.filter(
        (row) =>
            matchesQuery(row, term) &&
            (filters.levels.length === 0 ||
                filters.levels.includes(levelFilterOf(row.clientLevel))) &&
            (filters.trends.length === 0 ||
                (row.status !== null && filters.trends.includes(row.status))) &&
            inRange(row.purchasesCount, filters.minPurchases, filters.maxPurchases) &&
            inDayRange(row.lastPurchaseAt, filters.lastFrom, filters.lastTo),
    );
}

export function filterBySegment(
    rows: readonly PortalCustomerRow[],
    segment: CustomersSegment,
): PortalCustomerRow[] {
    return rows.filter(SEGMENT_TEST[segment]);
}

export function segmentCounts(rows: readonly PortalCustomerRow[]): SegmentCounts {
    const segments = Object.keys(SEGMENT_TEST) as CustomersSegment[];
    return Object.fromEntries(
        segments.map((segment) => [segment, rows.filter(SEGMENT_TEST[segment]).length]),
    ) as SegmentCounts;
}

function sortValue(row: PortalCustomerRow, key: SortKey): string | number | null {
    switch (key) {
        case 'customerName':
            return row.customerName ? normalizeName(row.customerName) : null;
        case 'favoriteStoreName':
            return row.favoriteStoreName ? normalizeName(row.favoriteStoreName) : null;
        case 'status':
            return row.status === null ? 0 : STATUS_RANK[row.status];
        case 'level':
            return LEVEL_RANK[row.clientLevel ?? ''] ?? 0;
        default:
            return row[key];
    }
}

/** Ordena sobre lo cargado. Sin dato va al final en los dos sentidos. */
export function sortCustomers(
    rows: readonly PortalCustomerRow[],
    sort: SortState,
): PortalCustomerRow[] {
    const factor = sort.direction === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
        const av = sortValue(a, sort.key);
        const bv = sortValue(b, sort.key);
        if (av === null || bv === null) return av === bv ? 0 : av === null ? 1 : -1;
        if (av === bv) return 0;
        return (av < bv ? -1 : 1) * factor;
    });
}

/** El mismo encabezado invierte; uno nuevo arranca A→Z en texto y de mayor a menor en cifras. */
export function nextSort(current: SortState, key: SortKey): SortState {
    if (current.key === key) {
        return { key, direction: current.direction === 'asc' ? 'desc' : 'asc' };
    }
    return { key, direction: TEXT_SORT_KEYS.has(key) ? 'asc' : 'desc' };
}

export function countActiveFilters(filters: PortalCustomersFilters): number {
    return [
        filters.query.trim() !== '',
        filters.levels.length > 0,
        filters.trends.length > 0,
        filters.minPurchases !== '' || filters.maxPurchases !== '',
        filters.lastFrom !== '' || filters.lastTo !== '',
    ].filter(Boolean).length;
}

/** Los filtros puestos que el servidor no aplica y solo miran lo cargado. */
export function localOnlyFilterLabels(filters: PortalCustomersFilters): string[] {
    const labels: string[] = [];
    if (isPhoneTerm(filters.query.trim())) labels.push('teléfono');
    if (filters.levels.length > 0) labels.push('nivel SK');
    if (filters.trends.length > 0) labels.push('tendencia');
    if (filters.minPurchases !== '' || filters.maxPurchases !== '') labels.push('compras contigo');
    if (filters.lastFrom !== '' || filters.lastTo !== '') labels.push('última compra contigo');
    return labels;
}

/** Prende o apaga un valor de un grupo de chips. */
export function toggleInList<T>(list: readonly T[], value: T): T[] {
    return list.includes(value) ? list.filter((item) => item !== value) : [...list, value];
}
